import { Component, OnDestroy, inject, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { EntryPagesDescriptionService } from "administration/entrypages/entry-pages-description.service";
import { PreferencesService } from "core/preferences.service";
import { EntryPagePreferences } from "dashboard/description/entry-page-preferences";
import { EntryPagesService } from "dashboard/description/entry-pages.service";
import { combineLatest, Observable, ReplaySubject, Subject, withLatestFrom } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { NavigationService } from "navigation/list/navigation.service";
import { AsyncPipe } from "@angular/common";
import { CardComponent } from "common/card/card.component";
import { TabsComponent } from "common/tab/tabs.component";
import { TabNoRouteComponent } from "common/tab/tab-no-route.component";
import { DescriptionComponent } from "common/editor/description.component";

type EntryPageTab = stages.entrypage.EntryPageTab;
const PREFERENCE_KEY = "entryPage";

@Component({
	selector: "stages-entry-pages",
	templateUrl: "./entry-pages.component.html",
	styleUrls: ["./entry-pages.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CardComponent, TabsComponent, TabNoRouteComponent, DescriptionComponent, AsyncPipe],
})
export class EntryPagesComponent implements OnDestroy {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly preferencesService = inject(PreferencesService);
	private entryPagesService = inject(EntryPagesService);
	descriptionService = inject(EntryPagesDescriptionService);
	readonly navigationService = inject(NavigationService);

	private destroy$ = new Subject<boolean>();
	paramMap$: Observable<ParamMap>;
	tabs$: Observable<EntryPageTab[]>;
	entryPageSubject = new ReplaySubject<stages.entrypage.EntryPage>(1);
	entryPage$ = this.entryPageSubject.asObservable();
	active!: string;

	constructor() {
		const route = this.route;
		const entryPagesService = this.entryPagesService;
		const navigationService = this.navigationService;

		this.paramMap$ = route.paramMap;
		this.tabs$ = this.route.data.pipe(map((data) => data.entryPageTabs));

		combineLatest([this.tabs$, this.paramMap$])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([entryPageTabs, paramMap]) => {
				const tabId = paramMap.get("tab");
				const tab$ = tabId === null ? this.getInitialTab(entryPageTabs) : this.getTabForId(entryPageTabs, tabId);
				tab$.then((tab) => {
					this.update(tab);
				});
			});

		navigationService.forceRefresh$
			.pipe(withLatestFrom(this.tabs$), takeUntil(this.destroy$))
			// eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: Remove this comment and fix warnings!
			.subscribe(async ([_refresh, tabs]) => {
				const activeEntryPage = this.getActiveTab(tabs);
				if (activeEntryPage) {
					this.entryPageSubject.next(await entryPagesService.refreshEntryPage(activeEntryPage));
				}
			});
	}

	tabClicked(entryPageTab: EntryPageTab): void {
		this.router.navigate([{ tab: entryPageTab.id }], { relativeTo: this.route });
	}

	getActiveTabName(tabs: EntryPageTab[]): string | undefined {
		return this.getActiveTab(tabs)?.name;
	}

	private getActiveTab(tabs: EntryPageTab[]): EntryPageTab | undefined {
		for (const tab of tabs) {
			if (tab.id === this.active) {
				return tab;
			}
		}
		return undefined;
	}

	private async update(tab: EntryPageTab): Promise<void> {
		const entryPage = await this.entryPagesService.getEntryPage(tab);
		this.active = entryPage.id;
		this.entryPageSubject.next(entryPage);
		this.savePreference(tab);
	}

	private async getInitialTab(tabs: EntryPageTab[]): Promise<EntryPageTab> {
		const preferences = await this.preferencesService.getPreference(PREFERENCE_KEY, new EntryPagePreferences());
		return this.getTabForId(tabs, preferences.tabId);
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	private async getTabForId(tabs: EntryPageTab[], selectedTabId: string | undefined): Promise<EntryPageTab> {
		let defaultTab: EntryPageTab | null = null;
		for (const tab of tabs) {
			if (selectedTabId && selectedTabId === tab.id) {
				return tab;
			}

			if (tab.defaultTab) {
				defaultTab = tab;
			}
		}

		return defaultTab === null ? tabs[0] : defaultTab;
	}

	private savePreference(tab: EntryPageTab): void {
		this.preferencesService.setPreference(PREFERENCE_KEY, {
			tabId: tab.id,
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}
}
